import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { flatRateBillingContract } from "@common/contracts/flate-rate-billing.contract";
import { CalculatePriceResponseDto } from "@common/dto/billing/flatRate/calculate-price-response.dto";
import { WorkspaceDto } from "@common/dto/workspace/workspace.dto";
import { Workspace } from "@common/types/workspace";
import { billingQueryKeys } from "@hooks/useBillingStatus";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { queryClient } from "../queryClient";
import {
    updateWorkspaceDetailsInQueryData,
    workspaceQueryKeys,
} from "./workspace.api";

const flatRateBillingContractClient = getBackendAPIClient(
    flatRateBillingContract,
);

const flatRateBillingQueryKeys = {
    all: () => ["flat-rate-billing"],
    prepareCheckout: () => [
        ...flatRateBillingQueryKeys.all(),
        "prepare-checkout",
    ],
    calculatedPrice: (
        workspaceId: string,
        payload: ServerInferRequest<
            typeof flatRateBillingContract.calculatePrice
        >["body"],
    ) => [
        ...flatRateBillingQueryKeys.all(),
        "calculate-price",
        workspaceId,
        payload,
    ],
    plans: () => [...flatRateBillingQueryKeys.all(), "plans"],
    entityBillingCount: (workspaceId: string) => [
        ...flatRateBillingQueryKeys.all(),
        "entity-billing-count",
        workspaceId,
    ],
} as const;

export function usePrepareWorkspaceCheckoutMutation() {
    return useMutation({
        mutationFn: async (workspaceId: string) => {
            const result =
                await flatRateBillingContractClient.prepareWorkspaceCheckout({
                    params: { workspaceId },
                });
            return result.body;
        },
    });
}

export function usePrepareOrganizationCheckoutMutation() {
    return useMutation({
        mutationFn: async (organizationId: string) => {
            const result =
                await flatRateBillingContractClient.prepareOrganizationCheckout(
                    {
                        params: { organizationId },
                    },
                );
            return result.body;
        },
    });
}

export function useStartEnterpriseTrialMutation(
    workspaceId: string,
    onSuccess?: () => void,
) {
    return useMutation({
        mutationFn: async () => {
            const result =
                await flatRateBillingContractClient.startEnterpriseTrial({
                    params: { workspaceId },
                });
            return WorkspaceDto.deserialize(result.body);
        },
        onSuccess(updatedWorkspace) {
            handleWorkspaceBillingUpdate(updatedWorkspace);
            onSuccess?.();
        },
    });
}

export function useCalculatePriceQuery(
    workspaceId: string,
    payload: ServerInferRequest<
        typeof flatRateBillingContract.calculatePrice
    >["body"],
) {
    return useQuery({
        queryKey: flatRateBillingQueryKeys.calculatedPrice(
            workspaceId,
            payload,
        ),
        queryFn: async () => {
            const result = await flatRateBillingContractClient.calculatePrice({
                params: { workspaceId },
                body: payload,
            });

            return CalculatePriceResponseDto.deserialize(result.body);
        },
        placeholderData: keepPreviousData,
    });
}

export function useSubscribeMutation(
    workspaceId: string,
    onSuccess?: () => void,
    onError?: (error: Error) => void,
) {
    return useMutation({
        mutationFn: async (
            payload: ServerInferRequest<
                typeof flatRateBillingContract.subscribe
            >["body"],
        ) => {
            const result = await flatRateBillingContractClient.subscribe({
                params: { workspaceId },
                body: payload,
            });
            return WorkspaceDto.deserialize(result.body);
        },
        onSuccess(updatedWorkspace) {
            handleWorkspaceBillingUpdate(updatedWorkspace);
            onSuccess?.();
        },
        onError(error) {
            queryClient.invalidateQueries({
                queryKey: billingQueryKeys.status(workspaceId),
            });
            onError?.(error);
        },
    });
}

export function useUpgradeMutation(
    workspaceId: string,
    onSuccess?: () => void,
) {
    return useMutation({
        mutationFn: async (
            payload: ServerInferRequest<
                typeof flatRateBillingContract.upgrade
            >["body"],
        ) => {
            const result = await flatRateBillingContractClient.upgrade({
                params: { workspaceId },
                body: payload,
            });
            return WorkspaceDto.deserialize(result.body);
        },
        onSuccess(updatedWorkspace) {
            handleWorkspaceBillingUpdate(updatedWorkspace);
            onSuccess?.();
        },
    });
}

export function useConvertMutation(
    workspaceId: string,
    onSuccess?: () => void,
) {
    return useMutation({
        mutationFn: async (
            payload: ServerInferRequest<
                typeof flatRateBillingContract.convert
            >["body"],
        ) => {
            const result = await flatRateBillingContractClient.convert({
                params: { workspaceId },
                body: payload,
            });
            return WorkspaceDto.deserialize(result.body);
        },
        onSuccess(updatedWorkspace) {
            handleWorkspaceBillingUpdate(updatedWorkspace);
            onSuccess?.();
        },
    });
}

export function useDowngradeMutation(
    workspaceId: string,
    onSuccess?: () => void,
) {
    return useMutation({
        mutationFn: async (
            payload: ServerInferRequest<
                typeof flatRateBillingContract.downgrade
            >["body"],
        ) => {
            await flatRateBillingContractClient.downgrade({
                params: { workspaceId },
                body: payload,
            });
        },
        onSuccess() {
            queryClient.invalidateQueries({
                queryKey: billingQueryKeys.status(workspaceId),
            });
            onSuccess?.();
        },
    });
}

function handleWorkspaceBillingUpdate(updatedWorkspace: Workspace) {
    updateWorkspaceDetailsInQueryData(
        updatedWorkspace.id,
        (workspaceDetails) => ({
            ...workspaceDetails,
            workspace: {
                ...workspaceDetails.workspace,
                ...updatedWorkspace,
                // Preserve loaded relations from cache (not returned by billing endpoints)
                entities: workspaceDetails.workspace.entities,
                userAssignments: workspaceDetails.workspace.userAssignments,
            },
        }),
    );

    queryClient.invalidateQueries({
        queryKey: workspaceQueryKeys.allQueries(),
    });

    queryClient.invalidateQueries({
        queryKey: billingQueryKeys.status(updatedWorkspace.id),
    });
}

interface UseEntityBillingCountQueryParams {
    workspaceId: string | null | undefined;
    enabled: boolean;
}

export function useEntityBillingCountQuery({
    workspaceId,
    enabled,
}: UseEntityBillingCountQueryParams) {
    return useQuery({
        queryKey: flatRateBillingQueryKeys.entityBillingCount(
            workspaceId ?? "",
        ),
        queryFn: async () => {
            if (!workspaceId) {
                throw new Error("workspaceId is required");
            }

            const result =
                await flatRateBillingContractClient.getEntityBillingCount({
                    params: { workspaceId },
                });
            return result.body;
        },
        enabled: !!workspaceId && enabled,
    });
}

/** Drives the V1/V2 -> V3 opt-in upgrade. Invalidates billing-status on success. */
export function useUpgradeToCatalogV3Mutation(
    workspaceId: string,
    onSuccess?: () => void,
    onError?: (error: Error) => void,
) {
    return useMutation({
        mutationFn: async () => {
            const result =
                await flatRateBillingContractClient.upgradeToCatalogV3({
                    params: { workspaceId },
                });
            // The axios client throws on non-2xx so result.status is always 200
            // here. 403/404/409 responses become AxiosErrors caught in onError.
            return WorkspaceDto.deserialize(result.body);
        },
        onSuccess(updatedWorkspace) {
            handleWorkspaceBillingUpdate(updatedWorkspace);
            // Explicitly re-fetch billing status so planCatalogVersion
            // reflects V3 immediately (staleTime: Infinity would otherwise
            // keep the V1 value cached and re-show the upgrade banner).
            // The refreshed status carries `licensedPriceItems` so the
            // billing UI lights up the V3 view.
            queryClient.invalidateQueries({
                queryKey: billingQueryKeys.status(workspaceId),
            });
            onSuccess?.();
        },
        onError(error: Error) {
            queryClient.invalidateQueries({
                queryKey: billingQueryKeys.status(workspaceId),
            });
            onError?.(error);
        },
    });
}
